export const INVOICE_ADJUSTMENT_DRAFT_VERSION = 2;

const asText = (value) => String(value ?? "").trim();

const normalizeIds = (values = []) =>
  Array.from(
    new Set(
      (Array.isArray(values) ? values : [])
        .map((value) => asText(value))
        .filter(Boolean),
    ),
  ).sort();

export const buildInvoiceAdjustmentContext = ({
  table = null,
  orderIds = [],
  orderNumber = "",
  lineIds = [],
} = {}) => {
  if (!table) return null;
  const isDelivery = Boolean(table.isDelivery);
  const tableRef = isDelivery
    ? table.deliveryRef ||
      table.orderNumber ||
      table.order_number ||
      table.name ||
      table.id
    : table.id ?? table.number ?? table.name;
  if (tableRef === null || tableRef === undefined || asText(tableRef) === "") {
    return null;
  }
  return {
    kind: isDelivery ? "delivery" : "table",
    tableRef: asText(tableRef),
    orderIds: normalizeIds(orderIds),
    orderNumber: asText(orderNumber),
    lineIds: normalizeIds(lineIds),
  };
};

export const areInvoiceAdjustmentContextsCompatible = (stored, current) => {
  if (!stored || !current) return false;
  if (stored.kind !== current.kind || stored.tableRef !== current.tableRef) {
    return false;
  }

  const storedOrders = normalizeIds(stored.orderIds);
  const currentOrders = normalizeIds(current.orderIds);
  if (storedOrders.length || currentOrders.length) {
    return (
      storedOrders.length > 0 &&
      currentOrders.length > 0 &&
      storedOrders.join("|") === currentOrders.join("|")
    );
  }

  if (stored.orderNumber || current.orderNumber) {
    return (
      Boolean(stored.orderNumber) &&
      Boolean(current.orderNumber) &&
      stored.orderNumber === current.orderNumber
    );
  }

  const storedLines = normalizeIds(stored.lineIds);
  const currentLines = normalizeIds(current.lineIds);
  return (
    storedLines.length > 0 &&
    currentLines.length > 0 &&
    storedLines.join("|") === currentLines.join("|")
  );
};

export const serializeInvoiceAdjustmentDraft = ({ context, adjustments }) =>
  JSON.stringify({
    version: INVOICE_ADJUSTMENT_DRAFT_VERSION,
    context,
    adjustments: Array.isArray(adjustments) ? adjustments : [],
    updatedAt: new Date().toISOString(),
  });

export const readInvoiceAdjustmentDraft = (raw, currentContext) => {
  if (!raw) return { adjustments: [], discard: false };
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    // Les anciens tableaux ne portaient aucune identité de service. Les
    // restaurer sur une table réutilisée pourrait appliquer une remise fantôme.
    if (Array.isArray(parsed)) return { adjustments: [], discard: true };
    if (
      Number(parsed?.version) !== INVOICE_ADJUSTMENT_DRAFT_VERSION ||
      !areInvoiceAdjustmentContextsCompatible(parsed?.context, currentContext)
    ) {
      return { adjustments: [], discard: true };
    }
    return {
      adjustments: Array.isArray(parsed.adjustments) ? parsed.adjustments : [],
      discard: false,
    };
  } catch (_error) {
    return { adjustments: [], discard: true };
  }
};
